/**
 * GuardianConsent - the page a PARENT lands on from their email
 * -------------------------------------------------------------
 * This is the only page on the site written for somebody who has never heard
 * of ChoreConnect, is not logged in, and did not choose to be here. So it
 * explains plainly what the service is, what their child would be doing, and
 * what we hold about them - before asking anything.
 *
 * Both answers are recorded. Declining is a real, equal option, not a
 * hidden link.
 *
 * WHAT IS LISTED HERE HAS TO MATCH WHAT THE SITE DOES. It did not, once: the
 * page asked about applying for chores long after the site had also gained
 * offers, messages and notifications. If you add anything that changes what
 * happens to a child here, change this list AND the matching one in
 * backend/routes/consent.js, and bump CONSENT_VERSION so every family is
 * asked again.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Briefcase,
  CalendarClock,
  MessageSquare,
  Bell,
  Repeat,
} from 'lucide-react';

import { consentApi } from '../services/api';
import { dateOnly } from '../utils/format';
import Loader from '../components/Loader';
import { useI18n } from '../hooks/useI18n';

export default function GuardianConsent() {
  const { t } = useI18n();
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [outcome, setOutcome] = useState(null); // 'confirmed' | 'declined'
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    consentApi
      .read(token)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const decide = async (confirm) => {
    setBusy(true);
    setError('');
    try {
      if (confirm) {
        await consentApi.confirm(token);
        setOutcome('confirmed');
      } else {
        await consentApi.decline(token);
        setOutcome('declined');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loader label={t('common.checkingLink')} />;

  // ---- Link no longer valid ----
  if (!data) {
    return (
      <div className="auth-layout">
        <div className="card">
          <div className="alert alert-error">
            <AlertTriangle size={20} aria-hidden="true" />
            <div>{error || t('consent.linkInvalid')}</div>
          </div>
          <p className="text-sm text-muted" style={{ marginTop: 'var(--space-sm)' }}>
            {t('consent.askChildResend')}
          </p>
        </div>
      </div>
    );
  }

  const student = data.student;

  /**
   * The four things being agreed to. Each one says what the child can do AND
   * the limit on it, because a parent deciding needs both: "adults can see
   * when my child is free" without "nothing is booked unless they agree" is a
   * frightening half of a sentence.
   */
  const agreeingTo = [
    {
      icon: Briefcase,
      title: t('consent.agree1Title'),
      body: t('consent.agree1Body', { name: student.firstName }),
    },
    {
      icon: Repeat,
      title: t('consent.agree2Title'),
      body: t('consent.agree2Body', { name: student.firstName }),
    },
    {
      icon: CalendarClock,
      title: t('consent.agree3Title'),
      body: t('consent.agree3Body', { name: student.firstName }),
    },
    {
      icon: MessageSquare,
      title: t('consent.agree4Title'),
      body: t('consent.agree4Body', { name: student.firstName }),
    },
    {
      icon: Bell,
      title: t('consent.agree5Title'),
      body: t('consent.agree5Body', { name: student.firstName }),
    },
  ];

  // ---- Decision made ----
  if (outcome) {
    const confirmed = outcome === 'confirmed';
    return (
      <div className="auth-layout">
        <div className="card">
          <div className={`alert ${confirmed ? 'alert-success' : 'alert-info'}`}>
            {confirmed ? <CheckCircle2 size={20} aria-hidden="true" /> : <Info size={20} aria-hidden="true" />}
            <div>
              {confirmed
                ? t('consent.thankYouConfirmed', { name: student.firstName })
                : t('consent.thankYouDeclined', { name: student.firstName })}
            </div>
          </div>

          {confirmed && (
            <p className="text-sm text-muted" style={{ marginTop: 'var(--space-sm)' }}>
              {t('consent.copyEmailed')}
            </p>
          )}

          <Link to="/" className="btn btn-outline btn-block" style={{ marginTop: 'var(--space-sm)' }}>
            {t('consent.findOutMore')}
          </Link>
        </div>
      </div>
    );
  }

  // ---- The decision itself ----
  return (
    <div className="container section" style={{ maxWidth: 680 }}>
      <div className="page-header text-center">
        <span className="feature-icon" style={{ margin: '0 auto var(--space-sm)' }}>
          <ShieldCheck size={28} aria-hidden="true" />
        </span>
        <h1>{data.renewal ? t('consent.titleRenewal') : t('consent.titleNew')}</h1>
        <p style={{ marginInline: 'auto' }}>
          {data.renewal
            ? t('consent.introRenewal', { name: student.firstName })
            : t('consent.introNew', { fullName: `${student.firstName} ${student.lastName}` })}
        </p>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-sm)' }}>{error}</div>}

      {/* A family who already said yes, to wording we have since changed. They
          are told what changed and what it costs them to do nothing - being
          asked the same question twice with no explanation reads like the site
          lost their answer. */}
      {data.renewal && (
        <div className="alert alert-warning" style={{ marginBottom: 'var(--space-md)' }}>
          <AlertTriangle size={20} aria-hidden="true" />
          <div>
            <strong>{t('consent.changedTitle')}</strong>{' '}
            {t('consent.changedBody', { name: student.firstName })}
            {data.previouslyConfirmedAt && (
              <> {t('consent.agreedOn', { date: dateOnly(data.previouslyConfirmedAt) })}</>
            )}{' '}
            {t('consent.untilYouAnswer', { name: student.firstName })}
          </div>
        </div>
      )}

      {data.alreadyConfirmed && (
        <div className="alert alert-info" style={{ marginBottom: 'var(--space-sm)' }}>
          <Info size={20} aria-hidden="true" />
          <div>{t('consent.already')}</div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 'var(--space-md)' }}>
        <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('consent.whatIsIt')}</h3>
        <p style={{ marginBottom: 'var(--space-sm)' }}>
          {t('consent.whatIsItBody')}
        </p>

        <div className="alert alert-info">
          <Info size={18} aria-hidden="true" />
          <div className="text-sm">
            <strong>{t('consent.introOnlyTitle')}</strong>{' '}
            {t('consent.introOnlyBody')}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 'var(--space-md)' }}>
        <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('consent.whatYouAgree')}</h3>

        <div className="consent-points">
          {agreeingTo.map(({ icon: Icon, title, body }) => (
            <div className="consent-point" key={title}>
              <span className="consent-point-icon" aria-hidden="true">
                <Icon size={20} />
              </span>
              <div>
                <strong>{title}</strong>
                <p className="text-sm text-muted" style={{ marginTop: 2 }}>{body}</p>
              </div>
            </div>
          ))}
        </div>

        <hr className="divider" />

        <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('consent.whatWeHold')}</h3>
        <div className="stack" style={{ gap: 10 }}>
          {[
            t('consent.hold0', { name: student.firstName }),
            t('consent.hold1'),
            t('consent.hold2'),
            t('consent.hold3'),
          ].map((item) => (
            <span key={item} className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
              <CheckCircle2 size={18} className="text-green" style={{ flexShrink: 0, marginTop: 3 }} aria-hidden="true" />
              <span className="text-sm">{item}</span>
            </span>
          ))}
        </div>

        <hr className="divider" />

        <p className="text-sm text-muted">
          {t('consent.fullDetail')} <Link to="/privacy">{t('consent.privacyNotice')}</Link>{' '}
          {t('consent.andThe')} <Link to="/terms">{t('consent.termsOfService')}</Link>{t('consent.readFirst')}
        </p>
      </div>

      <div className="card" style={{ marginBottom: 'var(--space-md)' }}>
        <h3 style={{ marginBottom: 'var(--space-sm)' }}>
          {t('consent.whatTheyToldUs', { name: student.firstName })}
        </h3>
        <div className="detail-list">
          <div className="detail-row">
            <span className="detail-label">{t('consent.name')}</span>
            <span>
              {student.firstName} {student.lastName}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">{t('consent.age')}</span>
            <span>{student.age}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">{t('consent.schoolYear')}</span>
            <span>{student.schoolYear}</span>
          </div>
          {student.school && (
            <div className="detail-row">
              <span className="detail-label">{t('consent.school')}</span>
              <span>{student.school}</span>
            </div>
          )}
        </div>

        <p className="text-sm text-muted" style={{ marginTop: 'var(--space-sm)' }}>
          {t('consent.ifWrong')}
        </p>
      </div>

      {!data.alreadyConfirmed && (
        <div className="card">
          <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('consent.yourDecision')}</h3>

          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={() => decide(true)}
            disabled={busy}
          >
            <CheckCircle2 size={18} />
            {busy
              ? t('common.saving')
              : data.renewal
                ? t('consent.agreeRenewal', { name: student.firstName })
                : t('consent.agreeNew', { name: student.firstName })}
          </button>

          <button
            type="button"
            className="btn btn-outline btn-block"
            onClick={() => decide(false)}
            disabled={busy}
            style={{ marginTop: 'var(--space-xs)' }}
          >
            <XCircle size={18} />
            {data.renewal ? t('consent.declineRenewal') : t('consent.declineNew')}
          </button>

          <p className="text-sm text-muted text-center" style={{ marginTop: 'var(--space-sm)' }}>
            You can change your mind at any time. Recorded {dateOnly(new Date())} against terms
            version {data.termsVersion}, consent wording {data.consentVersion}.
          </p>
        </div>
      )}
    </div>
  );
}
